import { StateParams, StateProvider, StateService } from "@uirouter/angularjs";
import {
  ReportingServer,
  vsName as ReportingServerName,
} from "../../reporting/server";

import { vsComponentName as MachinesComponentsViewsMainName } from "./components/views/main";
import {
  getResolveObjectFromGridConfiguration,
  GridConfiguration,
  vsComponentName as RecordGridViewComponentName,
  RecordGridViewService,
  vsServiceName as RecordGridViewServiceName,
} from "../../records/views/recordGridView";
import { Machine } from "./models/machine";
import {
  FormViewController,
  FormViewService,
  vsComponentName as FormViewComponentName,
  vsServiceName as FormViewServiceName,
  FormViewConfig,
  FormSubmitArgs,
} from "../../records/forms/formView";
import {
  CloneRecordResponse,
  IServiceResponse,
  ServiceResponse,
} from "../../records/server/query";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../records/server/serverUtility";
import { cloneDeep } from "lodash";
import { IPromise, IQService } from "angular";
import {
  CloneAndConversionService,
  vsServiceName as CloneAndConversionServiceName,
} from "./services/cloneAndConversionService";
import {
  machineConfigurationObjectName,
  machineEpromConfigurationObjectName,
  machineObjectName,
} from "../shared/names";
import { vsObjectName as machineConfigurationTypeName } from "./configs/machineConfigurationType";
import { MachineEditFormViewConfig } from "./configs/machineConfiguration";
import { vsComponentName as machineDetailSpecificComponentName } from "./components/views/detail";
import { vsComponentName as machineConfigurationTypeDetailComponentName } from "./components/views/configurationType/info";
import { MachineConfigurationType } from "./models/machineConfigurationType";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../records/grids/columns/columnDefinitionService";
import { vsComponentName as configurableGridFormViewComponentName } from "../../records/views/configurableGridFormView";
import {
  MachineConfiguration,
  MachineConfigurationView,
} from "./models/machineConfiguration";
import {
  GridSettingsService,
  vsName as GridSettingsServiceName,
} from "../../records/grids/settings/gridSettingsService";
import { GridSelectionChangedArgs } from "../../records/grids/controller";
import {
  ConfigurableGridFormViewComponentController,
  ConfigurableGridFormViewConfig,
} from "../../records/views/configurableGridFormView/controller";
import {
  MachineConfigurationCellClassService,
  vsServiceName as MachineConfigurationCellClassServiceName,
} from "../shared/machineConfigurationCellClassService";
import {
  vsServiceName as MachineConfigurableGridFormViewServiceName,
  MachineConfigurableGridFormViewService,
} from "../shared/utils/configurableRecordView";

import { vsObjectName as machineConfigurationFileName } from "./configs/machineConfigurationFile";
import {
  ChangeJournalModalService,
  vsServiceName as ChangeJournalModalServiceName,
} from "../../records/changeJournal/changeNoteModalService";
import { getChangeJournalViewStateDefinition } from "../../records/changeJournal/changeJournalViewComponent/registration";
import { ChangeJournalObject } from "../../records/changeJournal/changeJournalViewComponent/models";
import {
  vsServiceName as configurationTypeModalServiceName,
  MachineConfigurationTypeSubmitOptionsModalService,
} from "./services/machineConfigurationTypeSubmitOptionsModalService";
export function routeConfig($stateProvider: StateProvider) {
  $stateProvider
    .state("main.machines", {
      url: "machines",
      template: "<ui-view></ui-view>",
      abstract: true,
      resolve: {
        reportGroup: [
          ReportingServerName,
          function (reportingServer: ReportingServer) {
            return reportingServer.getReportGroup("machine").then((rg) => {
              return rg;
            });
          },
        ],
      },
    })
    .state("main.machines.files", {
      url: "files",
      component: configurableGridFormViewComponentName,
      resolve: {
        gridConfiguration: [
          RecordGridViewServiceName,
          GridSettingsServiceName,
          (
            recordGridViewService: RecordGridViewService,
            gridSettingsService: GridSettingsService,
          ) => {
            const gridConfiguration =
              recordGridViewService.getGridConfiguration(
                machineConfigurationFileName,
              )!;

            gridConfiguration.gridSettings =
              gridSettingsService.getGridSettings(
                Symbol(machineConfigurationFileName),
              );

            return gridConfiguration;
          },
        ],
        config: [
          "gridConfiguration",
          MachineConfigurationCellClassServiceName,
          "environment",
          MachineConfigurableGridFormViewServiceName,
          (
            gridConfiguration: GridConfiguration,
            machineConfigurationCellClassService: MachineConfigurationCellClassService,
            environment: { Date: Date },
            machineConfigurableGridFormViewService: MachineConfigurableGridFormViewService,
          ): ConfigurableGridFormViewConfig => ({
            defaultQuery: {
              CurrentConfigurationOrDateGreaterThanOrEqualTo: environment.Date,
            },
            gridConfiguration,
            additionalToggleButtons: [
              machineConfigurableGridFormViewService.futureAndCurrentConfigurationsOnlyToggleButton(
                true,
              ),
            ],
            initialized: (controller) => {
              machineConfigurationCellClassService.styleColumns(
                controller.config.gridConfiguration.columns,
                true,
              );
            },
          }),
        ],
      },
    })
    .state("main.machines.software", {
      url: "software",
      component: configurableGridFormViewComponentName,
      resolve: {
        gridConfiguration: [
          RecordGridViewServiceName,
          (recordGridViewService: RecordGridViewService): GridConfiguration => {
            const config = recordGridViewService.getGridConfiguration(
              machineEpromConfigurationObjectName,
            )!;
            return config;
          },
        ],
        config: [
          "environment",
          "gridConfiguration",
          MachineConfigurationCellClassServiceName,
          MachineConfigurableGridFormViewServiceName,
          (
            environment: {
              Date: Date;
            },
            gridConfiguration: GridConfiguration,
            machineConfigurationCellClassService: MachineConfigurationCellClassService,
            machineConfigurableGridFormViewService: MachineConfigurableGridFormViewService,
          ): ConfigurableGridFormViewConfig => {
            return {
              defaultQuery: {
                CurrentConfigurationOrDateGreaterThanOrEqualTo:
                  environment.Date,
              },
              gridConfiguration,
              additionalToggleButtons: [
                machineConfigurableGridFormViewService.futureAndCurrentConfigurationsOnlyToggleButton(
                  true,
                ),
              ],
              initialized: (controller) =>
                machineConfigurationCellClassService.styleColumns(
                  controller.config.gridConfiguration.columns,
                  true,
                ),
            };
          },
        ],
      },
    })
    .state("main.machines.grid", {
      url: "",
      component: MachinesComponentsViewsMainName,
      resolve: {
        gridConfiguration: [
          RecordGridViewServiceName,
          "$state",
          (
            recordGridViewService: RecordGridViewService,
            $state: StateService,
          ) => {
            const machineConfigurationGridConfiguration =
              recordGridViewService.getGridConfiguration(
                machineConfigurationObjectName,
              );
            const gridConfiguration =
              recordGridViewService.getGridConfiguration(machineObjectName)!;
            gridConfiguration.gridDataSource =
              machineConfigurationGridConfiguration?.gridDataSource;
            gridConfiguration.onAddNew = () => {
              return $state.go("^.add").then(() => { });
            };

            return gridConfiguration;
          },
        ],
      },
    })
    .state("main.machines.add", {
      url: "/add",
      component: FormViewComponentName,
      params: {
        modelMachineId: undefined,
        modelMachineConfigurationId: undefined,
      },
      resolve: {
        formViewConfig: [
          FormViewServiceName,
          (formViewService: FormViewService) => {
            return formViewService.getFormViewConfig(
              machineConfigurationObjectName,
              <MachineEditFormViewConfig>{
                machineFieldsEditable: true,
              },
            );
          },
        ],
        fields: [
          "formViewConfig",
          (formViewConfig: FormViewConfig) => formViewConfig.fields,
        ],
        model: [
          "$stateParams",
          CloneAndConversionServiceName,
          (
            $stateParams: StateParams,
            cloneAndConversionService: CloneAndConversionService,
          ) => {
            const modelMachineId = $stateParams.modelMachineId;
            const modelMachineConfigurationId =
              $stateParams.modelMachineConfigurationId;

            if (!modelMachineId) {
              return {
                Configuration: { Status: "Floor", ChangeType: "NewMachine" },
                Machine: {},
              };
            }

            const cloneConfiguration =
              cloneAndConversionService.getCloneConfiguration(
                modelMachineId,
                modelMachineConfigurationId,
              );

            return cloneConfiguration;
          },
        ],
        onSubmit: [
          FormViewServiceName,
          "$state",
          ChangeJournalModalServiceName,
          (
            formViewService: FormViewService,
            $state: StateService,
            changeJournalModalService: ChangeJournalModalService,
          ) => [
              "formViewController",
              "model",
              (formViewController: FormViewController, model: any) =>
                formViewService
                  .submitForm<ServiceResponse<Machine>>({
                    formObject: model,
                    formViewController,
                    method: "POST",
                    url: "/api/machines/Machine",
                  })
                  .then((result) =>
                    $state.go("main.machines.detail.redirect", {
                      machineId: result.Result.Id,
                    }),
                  ),
            ],
        ],
        onCancel: [
          "$state",
          ($state: StateService) => () => $state.go("^.grid"),
        ],
        title: [() => "Add Machine"],
      },
    })
    .state("main.machines.detail", {
      url: "/{machineId:int}",
      component: machineDetailSpecificComponentName,
      resolve: {
        machine: [
          ServerUtilityName,
          "$stateParams",
          function (serverUtility: ServerUtility, $stateParams: StateParams) {
            const machineId = $stateParams["machineId"];
            return serverUtility
              .getById<IServiceResponse<Machine>>({
                url: "/api/machines/Machine",
                id: machineId,
              })
              .then((r) => r.Result);
          },
        ],
        machineConfiguration: [
          "machine",
          (machine: Machine) =>
            machine.Configs?.find((c) => c.CurrentConfiguration),
        ],
      },
    })
    .state("main.machines.detail.redirect", {
      url: "/redirect",
      redirectTo: (transition) => {
        return {
          state: "main.machines.detail.configurations",
          params: transition.params(),
        };
      },
    })
    .state("main.machines.detail.edit", {
      url: "/edit/{machineConfigurationId:int}",
      component: FormViewComponentName,
      resolve: {
        formViewConfig: [
          FormViewServiceName,
          (formViewService: FormViewService) =>
            formViewService.getFormViewConfig(machineConfigurationObjectName, <
              MachineEditFormViewConfig
              >{ machineFieldsEditable: true }),
        ],
        model: [
          "machine",
          "$stateParams",
          (machine: Machine, $stateParams: StateParams) => {
            const configurationId = $stateParams.machineConfigurationId;
            const configuration = machine.Configs?.find(
              (c) => c.Id === configurationId,
            );

            const modelMachine = cloneDeep(machine);
            delete modelMachine.Configs;

            return {
              Machine: modelMachine,
              Configuration: cloneDeep(configuration),
            };
          },
        ],
        fields: [
          "formViewConfig",
          (formViewConfig: FormViewConfig) => formViewConfig.fields,
        ],
        onSubmit: [
          FormViewServiceName,
          "$q",
          "$state",
          ChangeJournalModalServiceName,
          (
            formViewService: FormViewService,
            $q: IQService,
            $state: StateService,
            changeJournalModalService: ChangeJournalModalService,
          ) => [
              "formViewController",
              "model",
              (formViewController: FormViewController, model: any) =>
                changeJournalModalService
                  .getChangeJournalNote()
                  .then((changeJournalEntry) => {
                    const { form } = formViewController;

                    const configurationModel = { ...model.Configuration };
                    const machineModel = { ...model.Machine };

                    changeJournalModalService.setChangeJournalEntry(
                      configurationModel,
                      changeJournalEntry,
                    );
                    changeJournalModalService.setChangeJournalEntry(
                      machineModel,
                      changeJournalEntry,
                    );

                    const updatePromises = [
                      formViewService.submitForm({
                        formObject: configurationModel,
                        formViewController: formViewController,
                        method: "PUT",
                        url: `/api/machines/MachineConfiguration/${configurationModel.Id}`,
                        validationPrefix: "Configuration",
                      }),
                    ];

                    const machineId = model.Machine.Id;
                    const anyDirtyMachineFields =
                      form
                        .$getControls()
                        .find(
                          (f) => f.$name?.startsWith("Machine.") && f.$dirty,
                        ) !== undefined;

                    if (anyDirtyMachineFields) {
                      updatePromises.push(
                        formViewService.submitForm({
                          formObject: machineModel,
                          formViewController: formViewController,
                          method: "PUT",
                          url: `/api/machines/Machine/${machineId}`,
                          validationPrefix: "Machine",
                        }),
                      );
                    }

                    return $q
                      .all(updatePromises)
                      .then(() =>
                        $state.go("^.redirect", { machineId }, { reload: true }),
                      );
                  }),
            ],
        ],
        onCancel: [
          "$stateParams",
          "$state",
          ($stateParams: StateParams, $state: StateService) => () =>
            $state.go("^.redirect", {
              machineId: $stateParams.machineId,
            }),
        ],
        title: [() => "Edit Machine"],
      },
    })
    .state("main.machines.configurationType", {
      url: "/configurationType",
      views: {
        "@^.^": {
          component: RecordGridViewComponentName,
        },
      },
      resolve: getResolveObjectFromGridConfiguration([
        RecordGridViewServiceName,
        ColumnDefinitionServiceName,
        "$state",
        (
          recordGridViewService: RecordGridViewService,
          columnDefinitionService: ColumnDefinitionService,
          $state: StateService,
        ) => {
          const config = recordGridViewService.getGridConfiguration(
            machineConfigurationTypeName,
          )!;
          config.columns = [
            columnDefinitionService.getNumberColDef({
              field: "Id",
              hide: true, // hidden but available for use
            }),
            ...(config.columns ?? []),
          ];
          config.columns = columnDefinitionService.addInfoEditColumns(
            config.columns,
            {
              editPermission: config.showAddButtonPermission,
              viewPermission: "machineView",
            },
          );
          config.onAddNew = () => $state.go(".add", {}).then(() => { });

          return config;
        },
      ]),
    })
    .state("main.machines.configurationType.add", {
      url: "/create",
      params: {
        cloneId: undefined,
      },
      views: {
        "@^.^.^": {
          component: FormViewComponentName,
        },
      },
      resolve: FormViewService.getResolveObjectFromFormViewConfig(
        [
          FormViewServiceName,
          "$state",
          ChangeJournalModalServiceName,
          (
            formViewService: FormViewService,
            $state: StateService,
            changeJournalModalService: ChangeJournalModalService,
          ) => {
            const config = formViewService.getFormViewConfig(
              machineConfigurationTypeName,
            )!;
            config.submitForm = (args) => {
              const model: any = args.model ?? {};

              //model.ChangeJournalEntry = {
              //  Note: "Machine Configuration Type Created",
              //};

              return Promise.resolve(model)
                .then((model) =>
                  formViewService.saveForm({
                    formObject: model,
                    formViewController: args.formViewController,
                    url: `/api/machines/MachineConfigurationType`,
                  }),
                )
                .then((r) =>
                  $state.go("^.info.redirect", { id: (<any>r).Result.Id }),
                );
            };
            return config;
          },
        ],
        [
          "$stateParams",
          ServerUtilityName,
          ($stateParams: StateParams, serverUtility: ServerUtility) => {
            const { cloneId } = $stateParams;
            if (!cloneId) return {};
            return serverUtility
              .sendApiRequest<
                ServiceResponse<CloneRecordResponse<MachineConfigurationType>>
              >({
                method: "GET",
                url: `/api/machines/MachineConfigurationType/${cloneId}/clone`,
              })
              .then((result) => {
                const configurationType = result.Result.Clone;
                configurationType.Name = undefined;
                return configurationType;
              });
          },
        ],
      ),
    })
    .state("main.machines.configurationType.info", {
      url: "/{id:int}",
      views: {
        "@^.^.^": {
          component: machineConfigurationTypeDetailComponentName,
        },
      },
      resolve: {
        machineConfigurationType: [
          ServerUtilityName,
          "$stateParams",
          (serverUtility: ServerUtility, $stateParams: StateParams) =>
            serverUtility
              .getById({
                url: "/api/machines/MachineConfigurationType",
                id: $stateParams.id,
              })
              .then((r) => (<any>r).Result),
        ],
      },
    })
    .state("main.machines.configurationType.info.redirect", {
      url: "/redirect",
      redirectTo: (transition) => {
        return {
          state: "main.machines.configurationType.info.machines",
          params: transition.params(),
        };
      },
    })
    .state(
      "main.machines.configurationType.info.changeLog",
      getChangeJournalViewStateDefinition({
        resolveObjects: [
          machineConfigurationTypeName,
          "$q",
          (
            machineConfigurationType: any,
            $q: IQService,
          ): IPromise<ChangeJournalObject[]> =>
            $q.when(<ChangeJournalObject[]>[
              <ChangeJournalObject>{
                id: {
                  ObjectId: machineConfigurationType.Id,
                  ObjectName: machineConfigurationTypeName,
                },
                object: machineConfigurationType,
              },
            ]),
        ],
      }),
    )
    .state("main.machines.configurationType.info.machines", {
      url: "/machines",
      component: configurableGridFormViewComponentName,
      resolve: {
        gridConfiguration: [
          RecordGridViewServiceName,
          ColumnDefinitionServiceName,
          GridSettingsServiceName,
          (
            recordGridViewService: RecordGridViewService,
            columnDefinitionService: ColumnDefinitionService,
            gridSettingsService: GridSettingsService,
          ) => {
            const config = recordGridViewService.getGridConfiguration(
              machineConfigurationObjectName,
            )!;

            config.gridSettings = gridSettingsService.getGridSettings(
              Symbol("main.machines.configurationType.info.machines"),
            );
            config.columns = [
              columnDefinitionService.getLinkColDef({
                colId: "detail",
                headerName: " ",
                iconClass: "fa fa-info-circle text-primary",
                stateExpression:
                  "main.machines.detail.redirect({machineId: data.MachineId})",
              }),
              ...(config.columns ?? []),
            ];

            return config;
          },
        ],
        config: [
          "gridConfiguration",
          "machineConfigurationType",
          "environment",
          MachineConfigurableGridFormViewServiceName,
          MachineConfigurationCellClassServiceName,
          (
            gridConfiguration: GridConfiguration,
            machineConfigurationType: MachineConfigurationType,
            environment: { Date: Date },
            machineConfigurableGridFormViewService: MachineConfigurableGridFormViewService,
            machineConfigurationCellClassService: MachineConfigurationCellClassService,
          ): ConfigurableGridFormViewConfig => {
            return {
              defaultQuery: {
                MachineConfigurationTypeId: machineConfigurationType.Id,
                CurrentConfigurationOrDateGreaterThanOrEqualTo:
                  environment.Date,
              },
              additionalToggleButtons: [
                machineConfigurableGridFormViewService.futureAndCurrentConfigurationsOnlyToggleButton(
                  true,
                ),
              ],
              initialized: (ctrl) => {
                machineConfigurationCellClassService.styleColumns(
                  ctrl.config.gridConfiguration.columns,
                  true,
                );
              },
              gridConfiguration,
              navButtonText: "Edit",
              navButtonIcon: "fa-pencil",
              navPath: ".edit",
              navButtonPermission: "machineEdit",
              showNavButton: true,
            };
          },
        ],
      },
    })
    .state("main.machines.configurationType.info.machines.edit", {
      url: "/edit",
      views: {
        "@^.^": {
          component: configurableGridFormViewComponentName,
        },
      },
      resolve: {
        //selectedIds: "<",
        // gridConfiguration: "<",
        // config: "<",
        // gridSelectionChanged: "&",

        selectedIds: [
          RecordGridViewServiceName,
          ServerUtilityName,
          "machineConfigurationType",
          (
            recordGridViewService: RecordGridViewService,
            serverUtility: ServerUtility,
            machineConfigurationType: MachineConfigurationType,
          ) => {
            const machineConfigurationConfig =
              recordGridViewService.getGridConfiguration(
                machineConfigurationObjectName,
              )!;
            return serverUtility
              .fetchAll<MachineConfiguration>({
                url: machineConfigurationConfig.gridDataSource!.url,
                query: {
                  MachineConfigurationTypeId: machineConfigurationType.Id,
                },
              })
              .then((result) => result.map((r) => r.MachineConfigurationId));
          },
        ],
        gridConfiguration: [
          RecordGridViewServiceName,
          GridSettingsServiceName,
          (
            recordGridViewService: RecordGridViewService,
            gridSettingsService: GridSettingsService,
          ) => {
            const gridConfiguration =
              recordGridViewService.getGridConfiguration(
                machineConfigurationObjectName,
              )!;

            gridConfiguration.gridSettings =
              gridSettingsService.getGridSettings(
                Symbol("machineConfigurationTypeMachines.edit"),
              );

            return gridConfiguration;
          },
        ],
        config: [
          ServerUtilityName,
          "machineConfigurationType",
          MachineConfigurationCellClassServiceName,
          "environment",
          "gridConfiguration",
          "selectedIds",
          MachineConfigurableGridFormViewServiceName,
          (
            serverUtility: ServerUtility,
            machineConfigurationType: MachineConfigurationType,
            machineConfigurationCellClassService: MachineConfigurationCellClassService,
            environment: { Date: Date },
            gridConfiguration: GridConfiguration,
            selectedIds: number[],
            machineConfigurableGridFormViewService: MachineConfigurableGridFormViewService,
          ): ConfigurableGridFormViewConfig => {
            return {
              navButtonIcon: "fa-pencil",
              navButtonText: "Done",
              navPath: "^",
              showNavButton: true,
              showSelectButtons: true,
              defaultQuery: {
                Ids: selectedIds.length === 0 ? [0] : selectedIds,
                CurrentConfigurationOrDateGreaterThanOrEqualTo:
                  environment.Date,
              },
              additionalToggleButtons: [
                machineConfigurableGridFormViewService.idDefaultQueryToggleButton(
                  true,
                ),
                machineConfigurableGridFormViewService.futureAndCurrentConfigurationsOnlyToggleButton(
                  true,
                ),
              ],
              initialized: (
                controller: ConfigurableGridFormViewComponentController,
              ) => {
                machineConfigurationCellClassService.styleColumns(
                  controller.config.gridConfiguration.columns,
                  true,
                );
              },
              gridSelectionChanged: (args: GridSelectionChangedArgs) => {
                const machineConfiguration = <MachineConfigurationView>(
                  args.data
                );
                const machineConfigurationTypeId = machineConfigurationType.Id;
                const machineConfigurationId =
                  machineConfiguration.MachineConfigurationId;
                const url = "/api/machines/MachineConfiguration";

                serverUtility.patch<MachineConfiguration>({
                  url,
                  patchObject: {
                    Id: machineConfigurationId,
                    MachineConfigurationTypeId: args.selected
                      ? machineConfigurationTypeId
                      : undefined,
                    ChangeJournalEntry: {
                      Note: "Updating Machine Configuration Type",
                    },
                  },
                });
              },
              gridConfiguration,
              selectedIds,
            };
          },
        ],
      },
    })
    .state("main.machines.configurationType.info.edit", {
      url: "/edit",
      component: FormViewComponentName,
      resolve: FormViewService.getResolveObjectFromFormViewConfig(
        [
          FormViewServiceName,
          "$state",
          configurationTypeModalServiceName,
          (
            formViewService: FormViewService,
            $state: StateService,
            machineConfigurationTypeSubmitOptionsModalService: MachineConfigurationTypeSubmitOptionsModalService,
          ) => {
            const config = formViewService.getFormViewConfig(
              machineConfigurationTypeName,
            )!;

            config.submitForm = (args: FormSubmitArgs) =>
              machineConfigurationTypeSubmitOptionsModalService
                .openModal()
                .then((options) => {
                  const model: any = (args.model ??= {});
                  (<any>args.model ?? {}).Options = options;
                  return formViewService.saveForm({
                    formObject: model,
                    formViewController: args.formViewController,
                    url: "/api/machines/MachineConfigurationType",
                  });
                })
                .then(() => {
                  $state.go("^.redirect", undefined, { reload: true });
                });

            return config;
          },
        ],
        [
          "machineConfigurationType",
          (machineConfigurationType: MachineConfigurationType) => {
            return cloneDeep(machineConfigurationType);
          },
        ],
        [
          "$state",
          ($state: StateService) => ["args", () => $state.go("^.redirect")],
        ],
      ),
    });
}
routeConfig.$inject = ["$stateProvider"];
